import { el, btn, backButton } from '@/ui/dom';
import { SP_COIN_SVG, XP_STAR_SVG } from '@/ui/currencyIcons';
import { LEADERBOARD_ENABLED } from '@/config/features';
import { sfxUiTick } from '@/audio/sfx';
import { hapticTick, hapticImpact } from '@/ui/haptics';
import { ITEM_ICONS, ITEM_NAMES } from '@/input/ItemButton';
import { IntroClip } from '@/ui/howto/IntroClip';
import {
  CHAPTERS,
  ITEM_CATEGORIES,
  ITEM_GUIDE,
  type ItemGuideEntry,
} from '@/ui/screens/howToPlayContent';
import {
  raceSkillAward,
  SKILL_TOUR_PODIUM_BONUS,
  SKILL_TIERS,
  skillTier,
} from '@/core/skillPoints';
import { VEHICLE_UNLOCKS } from '@/config/racers';
import { vehicleLabel } from '@/ui/vehicleCatalog';
import type { SaveData } from '@/core/SaveData';

/**
 * HowToPlayScreen.ts — HOW TO PLAY (M86, redesigned M86b): the four-chapter
 * interactive guide in the M65 black-and-gold editorial language.
 *
 * M86b: opening the guide plays a REAL-gameplay transition clip first (the
 * IntroClip mini-stage: the actual sports-car GLB dribbling the actual race
 * ball over a checkered finish) which fades into chapter 1 — the owner's
 * replacement for the M86 CSS strip that read like fake footage. Controls
 * are a FIFA-style action table (action left, buttons right) with the
 * device's own scheme first and desktop leading on ARROWS + SPACE; items are
 * colour-coded by role (speed/control/attack/defense/legend). No em dashes
 * anywhere in guide copy (owner rule, test-enforced in the content module).
 *
 * M114: the clip now hands off to the SHARED onboarding demo race before the
 * chapters (video → demo → text, every step skippable) — see HowToPlayShowOpts.
 */

type Device = 'touch' | 'keys' | 'pad';

/** M114 — how Flow drives the video → demo → chapters handoff. */
export interface HowToPlayShowOpts {
  /** Fired exactly ONCE per show() when the intro clip ends or is tap-skipped
   *  (skip the video → straight to the demo, owner rule): Flow launches the
   *  shared onboarding demo race. Reduced motion (the clip never plays) fires
   *  it immediately — the demo carries its own SKIP. The chapter-1 REPLAY
   *  chip plays the clip bare and can never re-fire this. */
  onDemo?: () => void;
  /** The post-demo return: land straight on the chapters, no clip. */
  startAtChapters?: boolean;
}

// ---- Local icon set (fill: currentColor, restyleable from CSS) -------------
const BALL_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9.4" fill="none" stroke="currentColor" stroke-width="1.8"/>' +
  '<path d="M12 8.2 15.6 10.8 14.2 15H9.8L8.4 10.8Z"/>' +
  '<path d="M12 2.8v5.4M15.6 10.8l5-1.7M14.2 15l3.1 4.4M9.8 15l-3.1 4.4M8.4 10.8l-5-1.7" ' +
  'fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
const POINTER_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 21 5.5 9.5a7.5 7.5 0 1 1 13 0Z" fill-opacity="0.35"/>' +
  '<path d="M14.6 4.9a4.6 4.6 0 1 0-5.3 7.4L12 16l2.7-3.7a4.6 4.6 0 0 0-.1-7.4Z"/></svg>';
const STAR_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 2.4 14.7 8.6l6.7.6-5 4.4 1.5 6.6L12 16.7l-5.9 3.5 1.5-6.6-5-4.4 6.7-.6Z"/></svg>';
const REPLAY_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M8.4 5.6 15.8 10.9a1.3 1.3 0 0 1 0 2.2L8.4 18.4A1.3 1.3 0 0 1 6.4 17.3V6.7a1.3 1.3 0 0 1 2-1.1Z"/></svg>';

export class HowToPlayScreen {
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly clip: IntroClip;
  private kickerText!: HTMLElement;
  private sub!: HTMLElement;
  private steps!: HTMLElement;
  private body!: HTMLElement;
  private foot!: HTMLElement;

  private onBack: () => void = () => {};
  private save: SaveData | null = null;
  private chapter = 0;
  private device: Device = 'keys';
  private selectedItem = 0;
  private visible = false;
  private reduced = false;
  // M114 — the video → demo handoff. `demoFired` is the once-per-show()
  // latch; `demoGen` invalidates a clip resolution from a SUPERSEDED show()
  // (hide() cancels the clip, which still resolves its promise).
  private onDemo: (() => void) | null = null;
  private demoFired = false;
  private demoGen = 0;

  // Swipe state (pointer events — the global touch-action:none never blocks these).
  private swipeX = 0;
  private swipeY = 0;
  private swipeId: number | null = null;

  private readonly onKey = (e: KeyboardEvent): void => {
    if (!this.visible) return;
    if (e.key === 'ArrowRight') this.turn(1);
    else if (e.key === 'ArrowLeft') this.turn(-1);
    else if (e.key === 'Escape') this.exit();
  };

  constructor(parent: HTMLElement) {
    this.root = el('div', 'screen screen-howto screen-cine', parent);
    this.root.style.display = 'none';
    this.panel = el('div', 'howto-panel', this.root);
    this.buildShell();
    // The clip mounts LAST inside the screen root, so it covers the panel and
    // its fade-out IS the reveal of chapter 1.
    this.clip = new IntroClip(this.root);
    if (new URLSearchParams(location.search).has('debug')) {
      (window as unknown as Record<string, unknown>).__howtoClip = this.clip;
    }
    window.addEventListener('keydown', this.onKey);
  }

  show(save: SaveData, onBack: () => void, opts?: HowToPlayShowOpts): void {
    this.save = save;
    this.onBack = onBack;
    this.onDemo = opts?.onDemo ?? null;
    this.demoFired = false;
    const gen = ++this.demoGen; // stale resolutions from an earlier show() die here
    this.chapter = 0;
    this.selectedItem = 0;
    this.reduced = save.settings.reducedMotion;
    // First-guess device tab: phones/tablets open on TOUCH, desktops on KEYS.
    this.device = window.matchMedia('(pointer: coarse)').matches ? 'touch' : 'keys';
    this.root.classList.toggle('reduced', this.reduced);
    this.renderChapter();
    this.root.style.display = '';
    this.visible = true;
    // M114: returning from the demo race lands straight on the chapters — the
    // video already played this visit (and reduced motion must not loop back
    // into the demo forever).
    if (opts?.startAtChapters) return;
    // The real-gameplay transition clip leads into the guide (M86b). M114:
    // when it ends OR is tap-skipped, the demo race follows (video → demo →
    // chapters). Reduced motion skips the video outright — straight to the
    // demo when one is wired, straight to chapter 1 as before otherwise.
    if (!this.reduced) {
      void this.clip.play().then(() => this.maybeStartDemo(gen));
    } else {
      this.maybeStartDemo(gen);
    }
  }

  /** M114 — hand off to the demo race exactly once per show(). The chapter-1
   *  REPLAY chip calls clip.play() bare, so a replay can never re-fire this;
   *  the generation + visibility guards kill resolutions from a superseded
   *  show() or a clip cancelled by hide(). */
  private maybeStartDemo(gen: number): void {
    if (gen !== this.demoGen || this.demoFired || !this.visible || !this.onDemo) return;
    this.demoFired = true;
    this.onDemo();
  }

  hide(): void {
    this.visible = false;
    this.root.style.display = 'none';
    // M114: a live clip must not keep rolling (hidden) after the guide
    // closes. cancel() resolves its play() promise — the generation/visible
    // guards above keep that stale resolution from launching the demo.
    this.clip.cancel();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    this.clip.dispose();
    this.root.remove();
  }

  // ---- Shell (built once): header · steps rail · body · footer -------------

  private buildShell(): void {
    // M114b — the guide's exit is the SAME chevron every other menu wears,
    // moved from the top-RIGHT X to the shared upper-left corner. It sits on
    // the ROOT (under the intro clip, which keeps covering it — see the
    // scoped z-index in ui.css) rather than in the centred panel, which would
    // have drifted it 320px inward at desktop width.
    // swipe: false — this screen already owns horizontal swipes (they turn
    // chapters), so a left-edge swipe must stay unambiguous.
    backButton(this.root, () => this.exit(), { label: 'Close', swipe: false });

    const head = el('div', 'howto-head', this.panel);

    const kicker = el('div', 'lb-kicker', head);
    el('span', 'mhead-rule', kicker);
    this.kickerText = el('span', 'howto-kicker-text', kicker);
    el('span', 'mhead-rule', kicker);
    const title = el('div', 'lb-title howto-title', head);
    title.textContent = 'HOW TO PLAY';
    title.dataset.text = 'HOW TO PLAY';
    this.sub = el('div', 'lb-sub howto-sub', head);

    this.steps = el('div', 'howto-steps', this.panel);
    CHAPTERS.forEach((c, i) => {
      const step = btn('', 'howto-step', this.steps, () => this.goTo(i));
      el('span', 'howto-step-n', step).textContent = String(i + 1);
      el('span', 'howto-step-label', step).textContent = c.label;
    });

    this.body = el('div', 'howto-body', this.panel);
    this.body.addEventListener('pointerdown', (e) => {
      this.swipeId = e.pointerId;
      this.swipeX = e.clientX;
      this.swipeY = e.clientY;
    });
    this.body.addEventListener('pointerup', (e) => {
      if (e.pointerId !== this.swipeId) return;
      this.swipeId = null;
      const dx = e.clientX - this.swipeX;
      const dy = e.clientY - this.swipeY;
      // A real horizontal swipe, not a tap or a vertical scroll gesture.
      if (Math.abs(dx) >= 56 && Math.abs(dx) > 2 * Math.abs(dy)) this.turn(dx < 0 ? 1 : -1);
    });

    this.foot = el('div', 'howto-foot', this.panel);
  }

  // ---- Navigation -----------------------------------------------------------

  private turn(dir: 1 | -1): void {
    const next = this.chapter + dir;
    if (next < 0 || next >= CHAPTERS.length) return;
    sfxUiTick();
    hapticImpact();
    this.chapter = next;
    this.renderChapter(dir);
  }

  private goTo(i: number): void {
    if (i === this.chapter) return;
    hapticImpact(); // btn() already played the tick pair; add the page-turn beat
    const dir = i > this.chapter ? 1 : -1;
    this.chapter = i;
    this.renderChapter(dir);
  }

  private exit(): void {
    this.onBack();
  }

  /** Repaint the dynamic parts for the current chapter. `dir` slides the body
   *  in from the side the reader is heading toward (0 = no slide, first show). */
  private renderChapter(dir: 0 | 1 | -1 = 0): void {
    const c = CHAPTERS[this.chapter];
    this.kickerText.textContent = c.kicker;
    this.sub.textContent = `CHAPTER ${this.chapter + 1} OF ${CHAPTERS.length} · ${c.label}`;

    [...this.steps.children].forEach((node, i) => {
      node.classList.toggle('on', i === this.chapter);
      node.classList.toggle('done', i < this.chapter);
    });

    this.body.replaceChildren();
    this.body.scrollTop = 0;
    if (c.id === 'objective') this.buildObjective();
    else if (c.id === 'controls') this.buildControls();
    else if (c.id === 'items') this.buildItems();
    else this.buildSkill();

    // Slide the fresh page in (CSS ignores it under reduced motion).
    if (dir !== 0) {
      this.body.style.setProperty('--slide-from', dir === 1 ? '26px' : '-26px');
      this.body.classList.remove('turn');
      void this.body.offsetWidth;
      this.body.classList.add('turn');
    }

    this.foot.replaceChildren();
    const last = this.chapter === CHAPTERS.length - 1;
    btn(this.chapter === 0 ? 'CLOSE' : 'BACK', 'btn btn-secondary howto-btn', this.foot, () => {
      if (this.chapter === 0) this.exit();
      else this.turn(-1);
    });
    btn(
      last ? "LET'S RACE" : 'NEXT',
      `btn ${last ? 'btn-gold' : 'btn-primary'} howto-btn howto-btn-next`,
      this.foot,
      () => {
        if (last) this.exit();
        else this.turn(1);
      },
    );
  }

  // ---- Chapter 1: OBJECTIVE --------------------------------------------------

  private buildObjective(): void {
    // The editorial statement card: the one rule that makes this game.
    const hero = el('div', 'howto-hero', this.body);
    el('div', 'howto-hero-kicker', hero).textContent = 'REMEMBER ONE THING';
    const l1 = el('div', 'howto-hero-line', hero);
    l1.textContent = 'FIRST BALL INTO THE GOAL'; // M157: the finish is a goal (matches the clip)
    el('div', 'howto-hero-line2', hero).textContent = 'WINS!';
    el('div', 'howto-hero-sub', hero).textContent =
      'The BALLS decide race positions, not the cars. Wherever your ball sits on the course is where you stand.';
    if (!this.reduced) {
      const replay = btn('', 'howto-replay', hero, () => void this.clip.play());
      const ic = el('span', 'howto-replay-ic', replay);
      ic.innerHTML = REPLAY_SVG;
      el('span', 'howto-replay-label', replay).textContent = 'REPLAY THE CLIP';
    }

    const facts: [string, string, string][] = [
      [
        BALL_SVG,
        'PUSH YOUR BALL',
        'Ride close to dribble it, ease off before corners so it does not run away, and strike it flat out to launch it down the straights.',
      ],
      [
        ITEM_ICONS.shield,
        'DEFEND YOUR LEAD',
        'The race fights back, and so can you: knock rival balls off line, bump their cars, and drop walls, mud and tackles to slow the pack.',
      ],
      [
        POINTER_SVG,
        'LOST THE BALL?',
        'A red arrow on the road points straight back to it, and rival balls track along the progress bar up top.',
      ],
    ];
    facts.forEach(([icon, title, desc], i) => {
      const row = el('div', 'howto-fact', this.body);
      row.style.setProperty('--stag', String(i));
      const tile = el('span', 'howto-fact-ic', row);
      tile.innerHTML = icon;
      const text = el('span', 'howto-fact-text', row);
      el('span', 'howto-fact-title', text).textContent = title;
      el('span', 'howto-fact-desc', text).textContent = desc;
    });
  }

  // ---- Chapter 2: CONTROLS ----------------------------------------------------

  private buildControls(): void {
    // The device's own scheme leads the tab row (owner: prioritize by device).
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const order: [Device, string][] = coarse
      ? [
          ['touch', 'TOUCH'],
          ['keys', 'KEYBOARD'],
          ['pad', 'GAMEPAD'],
        ]
      : [
          ['keys', 'KEYBOARD'],
          ['pad', 'GAMEPAD'],
          ['touch', 'TOUCH'],
        ];
    const tabs = el('div', 'howto-devrow', this.body);
    order.forEach(([id, label]) => {
      const chip = btn(label, `chip howto-dev${this.device === id ? ' on' : ''}`, tabs, () => {
        if (this.device === id) return;
        this.device = id;
        this.buildControlTable(list);
        [...tabs.children].forEach((n, i) => n.classList.toggle('on', order[i][0] === id));
      });
      chip.setAttribute('aria-pressed', String(this.device === id));
    });

    const list = el('div', 'howto-devbody', this.body);
    this.buildControlTable(list);
  }

  /** FIFA-style action table: action name left, buttons right; the primary
   *  binding leads, alternates sit dimmed behind an "or". */
  private buildControlTable(list: HTMLElement): void {
    list.replaceChildren();
    const table = el('div', 'howto-ctl-table', list);

    const keycaps = (labels: string[], cls = ''): HTMLElement => {
      const wrap = document.createElement('span');
      wrap.className = `howto-kgroup${cls ? ` ${cls}` : ''}`;
      labels.forEach((k) => {
        const cap = document.createElement('span');
        cap.className = `howto-key${k.length > 2 ? ' wide' : ''}`;
        cap.textContent = k;
        wrap.appendChild(cap);
      });
      return wrap;
    };
    const row = (
      action: string,
      hint: string,
      primary: HTMLElement,
      alt: HTMLElement | null,
      i: number,
    ): void => {
      const r = el('div', 'howto-ctl-row', table);
      r.style.setProperty('--stag', String(i));
      const left = el('span', 'howto-ctl-action', r);
      el('span', 'howto-ctl-name', left).textContent = action;
      if (hint) el('span', 'howto-ctl-hint', left).textContent = hint;
      const right = el('span', 'howto-ctl-keys', r);
      right.appendChild(primary);
      if (alt) {
        el('span', 'howto-or', right).textContent = 'or';
        alt.classList.add('alt');
        right.appendChild(alt);
      }
    };

    if (this.device === 'keys') {
      // Desktop priority (owner): ARROWS + SPACE lead, letters are the alternate.
      row('ACCELERATE', '', keycaps(['↑']), keycaps(['W']), 0);
      row('BRAKE / REVERSE', 'hold to back up', keycaps(['↓']), keycaps(['S']), 1);
      row('STEER', '', keycaps(['←', '→']), keycaps(['A', 'D']), 2);
      row('USE ITEM', 'one tap, one fire', keycaps(['SPACE']), keycaps(['SHIFT', 'E']), 3);
      row('PAUSE', 'offline only', keycaps(['ESC']), keycaps(['P']), 4);
    } else if (this.device === 'pad') {
      row('ACCELERATE', '', keycaps(['RT']), null, 0);
      row('BRAKE / REVERSE', 'hold to back up', keycaps(['LT']), null, 1);
      row('STEER', '', keycaps(['L STICK']), null, 2);
      row('USE ITEM', 'one tap, one fire', keycaps(['A']), keycaps(['B', 'RB']), 3);
      row('PAUSE', 'offline only', keycaps(['START']), null, 4);
    } else {
      // Touch: the two real on-screen controls, mocked in miniature.
      const joy = (down: boolean): HTMLElement => {
        const j = document.createElement('span');
        j.className = `howto-joy${down ? ' down' : ''}`;
        const knob = document.createElement('span');
        knob.className = 'howto-joy-knob';
        j.appendChild(knob);
        return j;
      };
      const itemBtn = document.createElement('span');
      itemBtn.className = 'howto-itembtn';
      itemBtn.innerHTML = ITEM_ICONS.boost;
      row('STEER + GAS', 'touch low-left, push up and sideways', joy(false), null, 0);
      row('BRAKE / REVERSE', 'pull back, keep holding to reverse', joy(true), null, 1);
      row('USE ITEM', 'the big button low-right is your slot', itemBtn, null, 2);
      row('PAUSE', 'the pause chip at the top of the HUD', keycaps(['❚❚']), null, 3);
      // M115: tilt steering was retired as an option, so the guide no longer
      // points players at a scheme switch that isn't there any more.
      el('div', 'howto-ctl-note', list).textContent =
        'Left-handed layout and steering sensitivity both live in SETTINGS.';
    }
  }

  // ---- Chapter 3: ITEMS -------------------------------------------------------

  private buildItems(): void {
    el('div', 'howto-intro', this.body).textContent =
      'Drive through a ? box to roll an item. Your race position loads the dice: ' +
      'leaders draw defense, the back of the pack draws the comeback kit.';

    // The colour legend, explicitly LABELLED as the category system (M86c) —
    // one chip per role, in the same order the grid clusters below.
    const catOrder = Object.keys(ITEM_CATEGORIES) as (keyof typeof ITEM_CATEGORIES)[];
    const cats = el('div', 'howto-cats', this.body);
    el('div', 'howto-cats-head', cats).textContent = 'FIVE ITEM CATEGORIES';
    const legend = el('div', 'howto-legend', cats);
    for (const cat of catOrder) {
      const meta = ITEM_CATEGORIES[cat];
      const chip = el('span', 'howto-legend-chip', legend);
      chip.style.setProperty('--item-accent', meta.color);
      chip.textContent = meta.label;
    }

    // Cluster the gallery BY CATEGORY so the colours group and the system
    // reads at a glance (the guide's coverage test keeps ITEM_GUIDE itself in
    // HUD roulette order — this is display order only).
    const entries = [...ITEM_GUIDE].sort(
      (a, b) => catOrder.indexOf(a.cat) - catOrder.indexOf(b.cat),
    );

    const detail = el('div', 'howto-item-detail', this.body);
    const grid = el('div', 'howto-items', this.body);
    const cards: HTMLButtonElement[] = [];

    const select = (idx: number, entry: ItemGuideEntry): void => {
      this.selectedItem = idx;
      cards.forEach((cardEl, i) => cardEl.classList.toggle('on', i === idx));
      const meta = ITEM_CATEGORIES[entry.cat];
      detail.style.setProperty('--item-accent', meta.color);
      detail.replaceChildren();
      const ic = el('span', 'howto-detail-ic', detail);
      ic.innerHTML = ITEM_ICONS[entry.kind];
      const text = el('span', 'howto-detail-text', detail);
      const nameRow = el('span', 'howto-detail-namerow', text);
      el('span', 'howto-detail-name', nameRow).textContent = ITEM_NAMES[entry.kind].toUpperCase();
      const catChip = el('span', 'howto-cat', nameRow);
      catChip.textContent = meta.label;
      el('span', 'howto-who', nameRow).textContent = entry.who;
      el('span', 'howto-detail-desc', text).textContent = entry.desc;
      detail.classList.remove('pop');
      void detail.offsetWidth;
      detail.classList.add('pop');
    };

    entries.forEach((entry, i) => {
      const card = el('button', 'howto-item', grid);
      card.type = 'button';
      card.style.setProperty('--stag', String(i));
      card.style.setProperty('--item-accent', ITEM_CATEGORIES[entry.cat].color);
      const ic = el('span', 'howto-item-ic', card);
      ic.innerHTML = ITEM_ICONS[entry.kind];
      el('span', 'howto-item-name', card).textContent = ITEM_NAMES[entry.kind];
      // Every card wears its category tag (M86c: the coding must be unmissable).
      el('span', 'howto-item-cat', card).textContent = ITEM_CATEGORIES[entry.cat].label;
      card.addEventListener('click', () => {
        if (this.selectedItem === i) return;
        sfxUiTick();
        hapticTick();
        select(i, entry);
      });
      cards.push(card);
    });

    const idx = Math.min(this.selectedItem, entries.length - 1);
    select(idx, entries[idx]);
  }

  // ---- Chapter 4: XP & SP (M131) ----------------------------------------------

  private buildSkill(): void {
    const points = this.save?.lifetimePoints ?? 0;
    const firstUnlock = VEHICLE_UNLOCKS[0];

    // The two pools FIRST — the whole chapter exists to make this one
    // distinction land: XP is progress you can only earn; SP is a balance
    // you spend (and can top up). Gold star vs green coin, always.
    const cur = el('div', 'howto-cur', this.body);
    const curCard = (
      cls: string,
      icon: string,
      tag: string,
      title: string,
      lines: string[],
      i: number,
    ): void => {
      const card = el('div', `howto-cur-card ${cls}`, cur);
      card.style.setProperty('--stag', String(i));
      const head = el('div', 'howto-cur-head', card);
      const ic = el('span', 'howto-cur-ic', head);
      ic.innerHTML = icon;
      el('span', 'howto-cur-tag', head).textContent = tag;
      el('div', 'howto-cur-title', card).textContent = title;
      const ul = el('div', 'howto-cur-lines', card);
      for (const line of lines) el('div', 'howto-cur-line', ul).textContent = line;
    };
    curCard(
      'xp',
      XP_STAR_SVG,
      'XP',
      'EXPERIENCE POINTS',
      [
        'Earned by racing. Never spent, never bought.',
        `Unlocks your rides: the first at ${firstUnlock.points} XP (${vehicleLabel(firstUnlock.id).toUpperCase()}).`,
        LEADERBOARD_ENABLED
          ? 'Sets your rank and lifts you up the world rankings.'
          : 'Sets your rank, ROOKIE to LEGEND, on your Racer profile.',
      ],
      0,
    );
    curCard(
      'sp',
      SP_COIN_SVG,
      'SP',
      'SHOP POINTS',
      [
        'Earned by racing, one for one with XP.',
        'Spend it in the shop: paint jobs, flag wraps, wheels, mods.',
        'Spending never drops your rank or re-locks a ride. Top up in the shop.',
      ],
      1,
    );

    // Payout card — every number computed from the LIVE tuning tables, so a
    // rebalance session can never leave the guide teaching stale maths. One
    // award, two pools: every row pays the same amount of XP and SP.
    const pay = el('div', 'howto-paycard', this.body);
    const payHead = el('div', 'howto-pay-head', pay);
    const star = el('span', 'howto-pay-star', payHead);
    star.innerHTML = STAR_SVG;
    el('span', 'howto-pay-title', payHead).textContent = 'EVERY FINISH PAYS XP AND SP';
    const rows = el('div', 'howto-pay-rows', pay);
    const payRow = (label: string, value: string, note: string, i: number): void => {
      const r = el('div', 'howto-pay-row', rows);
      r.style.setProperty('--stag', String(i));
      el('span', 'howto-pay-label', r).textContent = label;
      el('span', 'howto-pay-note', r).textContent = note;
      el('span', 'howto-pay-value', r).textContent = value;
    };
    ([1, 2, 3] as const).forEach((place, i) => {
      const a = raceSkillAward(place);
      payRow(
        ['1ST', '2ND', '3RD'][i],
        `+${a.total} XP · +${a.total} SP`,
        `${a.base} + ${a.podiumBonus} podium bonus`,
        i,
      );
    });
    payRow('ANY FINISH', '+1 OR MORE', 'no zero days, every race counts', 3);
    payRow(
      'TOUR TOP 3',
      `+${SKILL_TOUR_PODIUM_BONUS[1]}/${SKILL_TOUR_PODIUM_BONUS[2]}/${SKILL_TOUR_PODIUM_BONUS[3]}`,
      'championship bonus at the tour finale',
      4,
    );

    // The XP rank ladder, with YOUR current rank lit.
    el('div', 'howto-tiers-head', this.body).textContent = 'XP RANKS';
    const ladder = el('div', 'howto-tiers', this.body);
    const mine = skillTier(points).name;
    SKILL_TIERS.forEach((t, i) => {
      const cell = el('div', `howto-tier${t.name === mine ? ' me' : ''}`, ladder);
      cell.style.setProperty('--tier-accent', t.color);
      cell.style.setProperty('--stag', String(i));
      el('span', 'lb-tier', cell).textContent = t.name;
      el('span', 'howto-tier-min', cell).textContent = t.min === 0 ? 'START' : `${t.min} XP`;
      if (t.name === mine) el('span', 'howto-tier-you', cell).textContent = 'YOU';
    });
  }
}
